import { useRef, useEffect, useState } from 'react';
import { GalaxyScene, SceneManager } from '../../visualization/scene/GalaxyScene';
import { SceneConfig } from '../../visualization/scene/SceneConfig';
import { TransactionEvent } from '../../visualization/elements/VisualElementsManager';
import { visualizationEventBus } from '../../visualization/events/VisualizationEventBus';
import { useGalaxyStore } from '../../core/state/GalaxyStore';

export function useGalaxyScene(config?: Partial<SceneConfig>): {
  sceneRef: React.RefObject<HTMLDivElement | null>;
  scene: SceneManager | null;
  isReady: boolean;
} {
  const sceneRef = useRef<HTMLDivElement | null>(null);
  const [scene, setScene] = useState<SceneManager | null>(null);
  const [isReady, setIsReady] = useState(false);
  
  useEffect(() => {
    if (!sceneRef.current) return undefined;
    
    const galaxyScene = new GalaxyScene(config);
    galaxyScene.initialize(sceneRef.current);
    
    setScene(galaxyScene);
    setIsReady(true);
    
    // Connect to real store data instead of simulation
    const connectToRealData = (): (() => void) | undefined => {
      const visualElements = galaxyScene.getVisualElementsManager();
      if (!visualElements) return undefined;
      
      // Connect store subscriptions
        
        // Subscribe to network stats updates
        const unsubscribeNetwork = useGalaxyStore.subscribe(
          (state) => ({
            hashrate: state.network.hashrate,
            difficulty: state.network.difficulty,
            mempool: state.mempool,
          }),
          (current, previous) => {
            if (current.hashrate !== previous.hashrate || current.difficulty !== previous.difficulty) {
              // Emit network stats event via event bus
              visualizationEventBus.emit('network_stats', {
                hashrate: current.hashrate,
                difficulty: current.difficulty,
              });
            }
          }
        );
        
        // Subscribe to new transactions
        const unsubscribeTransactions = useGalaxyStore.subscribe(
          (state) => state.blockchain.transactions,
          (transactions, prevTransactions) => {
            // Handle new transactions
            for (const [txid, tx] of transactions) {
              if (!prevTransactions.has(txid)) {
                // Emit transaction event via event bus
                visualizationEventBus.emit('transaction', {
                  id: txid,
                  value: tx.amount || 0,
                  feeRate: tx.feeRate || 0,
                  spawnedAt: tx.timestamp || Date.now(),
                } as TransactionEvent);
              }
            }
          }
        );
        
        // Subscribe to new blocks
        const unsubscribeBlocks = useGalaxyStore.subscribe(
          (state) => state.blockchain.blocks,
          (blocks, prevBlocks) => {
            // Handle new blocks
            for (const [blockHeight, block] of blocks) {
              if (!prevBlocks.has(blockHeight)) {
                // Emit block event via visualization event bus
                visualizationEventBus.emit('block', {
                  height: block.height,
                  size: block.size,
                  timestamp: block.timestamp,
                });
              }
            }
          }
        );
        
        // Initial data load from current store state
        const currentState = useGalaxyStore.getState();
        if (currentState.network.hashrate > 0) {
          // TODO: Fix API - visualElements.handleNetworkStatsUpdate({
          //   hashrate: currentState.network.hashrate,
          //   mempool: { usage: (currentState.mempool.size / 1000000) * 100 },
          // });
        }
        
      return () => {
        unsubscribeNetwork();
        unsubscribeTransactions();
        unsubscribeBlocks();
      };
    };
    
    // Connect to real data after a brief delay
    let storeCleanup: (() => void) | undefined;
    const connectTimeout = setTimeout(() => {
      storeCleanup = connectToRealData();
    }, 1000);
    
    return () => {
      galaxyScene.dispose();
      setScene(null);
      setIsReady(false);
      clearTimeout(connectTimeout);
      storeCleanup?.();
    };
  }, [config]);
  
  return {
    sceneRef,
    scene,
    isReady,
  };
}